import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  Paper,
  Container,
  Stack,
  Link,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Alert,
  Snackbar,
} from "@mui/material";
import { styled } from "@mui/system";
import { IoSend } from "react-icons/io5";
import { IoAttach } from "react-icons/io5";
import { FaCheck, FaCheckDouble } from "react-icons/fa";
import CustomBreadcrumb from "../../components/customBreadcrumb/CustomBreadcrumb";
import Title from "../../components/title/Title";
import axios from "axios";
import { useLocation, useParams } from "react-router-dom";
import MainLoader from "../../components/loading/MainLoader";
import { BASE_URL } from "../../const/URL";

// Interface for Attachment
interface Attachment {
  id: number;
  fileName: string;
  uploadedBy: string;
  createdAt: string;
}

// Interface for User Status
interface UserStatus {
  userId: number;
  type: string;
  isRead: boolean;
  readAt: string | null;
  username: string;
}

// Interface for Message
interface Message {
  id: number;
  sender: string;
  avatar: string;
  message: string;
  timestamp: string;
  messageType: number;
  isOwn: boolean;
  attachments?: Attachment[];
  userStatus?: UserStatus[];
}

// Styled Components
const ChatContainer = styled(Box)({
  height: "60vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#f5f5f5",
  borderRadius: "12px",
  overflow: "hidden",
});

const MessagesArea = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "20px",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

interface MessageBubbleProps {
  isOwn: boolean;
}

const MessageBubble = styled(Paper)<MessageBubbleProps>(({ isOwn }) => ({
  padding: "12px 16px",
  maxWidth: "70%",
  width: "fit-content",
  borderRadius: "12px",
  backgroundColor: isOwn ? "#1976d2" : "#ffffff",
  color: isOwn ? "#ffffff" : "#000000",
  alignSelf: isOwn ? "flex-end" : "flex-start",
  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
}));

const NotificationBubble = styled(Box)({
  padding: "8px 12px",
  margin: "8px auto",
  borderRadius: "8px",
  backgroundColor: "#e1f3fb",
  color: "#54656f",
  fontSize: "0.8rem",
  textAlign: "center",
  maxWidth: "80%",
  boxShadow: "0 1px 0.5px rgba(0,0,0,0.13)",
});

const InputArea = styled(Box)({
  padding: "20px",
  backgroundColor: "#ffffff",
  borderTop: "1px solid #e0e0e0",
});

// API URLs
const API_URL = `${BASE_URL}/api/v1/messages-conversation`;
const API_URL_REPLY = `${BASE_URL}/api/v1/conversation-message/reply`;
const API_URL_ATTACHMENT = `${BASE_URL}/api/v1/attachments-upload`;
const API_URL_ATTACHMENT_DOWNLOAD = `${BASE_URL}/api/v1/attachments-download`;

// GroupChat Component
const GroupChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState<string>("");
  const [messageId, setMessageId] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const { conversationID } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState<number | null>(
    null
  );

  const { state } = useLocation();

  const subject = (state as any)?.subject;
  const status = (state as any)?.status;

  // Fetch messages from the API
  const fetchMessages = useCallback(async () => {
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const currentUser = localStorage.getItem("UserID");

      const response = await axios.get(`${API_URL}/${conversationID}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.isSuccess && response.data.dataBundle) {
        const data = response.data.dataBundle;
        const formattedMessages = data.map((msg: any) => ({
          id: msg.id,
          sender: msg?.internalSender ? msg?.senderName : msg?.senderName,
          avatar: msg?.internalSender
            ? `https://ui-avatars.com/api/?name=${msg?.senderName?.charAt(
                0
              )}&background=B2C9AD&color=fff`
            : `https://ui-avatars.com/api/?name=${msg?.senderName?.charAt(
                0
              )}&background=B3C8CF&color=fff`,
          message: msg.body,
          timestamp: new Date(msg.createdAt).toLocaleTimeString([], {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          }),
          messageType: msg.messageType,
          isOwn: msg.externalSender !== null,
          attachments: msg.attachments || [],
          userStatus: msg.userStatus || [],
        }));

        // Check and update isRead status for the current user
        for (const message of formattedMessages) {
          const userStatus = message.userStatus.find(
            (status: UserStatus) => status.userId === Number(currentUser)
          );

          if (userStatus && !userStatus.isRead) {
            try {
              // Update isRead status via API
              await axios.put(
                `${BASE_URL}/api/v1/messages/update-status/${message.id}`,
                {},
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                  },
                }
              );
            } catch (error) {
              console.error(
                `Error updating read status for message ${message.id}:`,
                error
              );
            }
          }
        }

        setMessages(formattedMessages);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching messages:", error);
    }
  }, [conversationID]);

  // Fetch messages on component mount
  useEffect(() => {
    setIsLoading(true);
    fetchMessages();
  }, [fetchMessages]);

  const [alertOpen, setAlertOpen] = useState(false);

  const handleAttachFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      const maxSize = 2 * 1024 * 1024; // 2MB

      if (selectedFile.size > maxSize) {
        setAlertOpen(true);
        event.target.value = "";
      } else {
        setFile(selectedFile);
      }
    }
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  // Handle sending a message
  const handleSendMessage = async () => {
    if (newMessage.trim() || file) {
      try {
        const token = localStorage.getItem("enterprise_portal_access_Token");

        // Send the message
        const response = await axios.post(
          API_URL_REPLY,
          {
            conversationId: conversationID,
            messageBody: newMessage === "" ? " " : newMessage,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        const messageID = response.data.dataBundle.messageId;

        // If there's a file, upload it
        if (file) {
          const formData = new FormData();
          formData.append("file", file);

          await axios.post(`${API_URL_ATTACHMENT}/${messageID}`, formData, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          });
        }

        setNewMessage("");
        setFile(null);
        // Re-fetch messages after sending a new one
        await fetchMessages();
      } catch (error) {
        console.error("Error sending message:", error);
      }
    }
  };

  // Handle attachment download
  const handleDownloadAttachment = async (attachmentId: string) => {
    try {
      const token = localStorage.getItem("enterprise_portal_access_Token");
      const response = await axios.get(
        `${API_URL_ATTACHMENT_DOWNLOAD}/${attachmentId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `attachment_${attachmentId}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error downloading attachment:", error);
    }
  };

  return (
    <div className="page-container">
      {/* Main Loader */}
      <MainLoader show={isLoading} />
      <CustomBreadcrumb
        items={[
          { path: "/", title: "Home" },
          { path: "/messages/inbox", title: "Messages" },
          { path: "/messages/inbox/chat", title: "Chat" },
        ]}
      />
      <Title>Messages - {subject}</Title>

      {/* MUI Alert in Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={3000}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        onClose={handleCloseAlert}
      >
        <Alert severity="error" onClose={handleCloseAlert}>
          File size exceeds the 2MB limit.
        </Alert>
      </Snackbar>

      <Container maxWidth="xl">
        <ChatContainer>
          <MessagesArea>
            {messages.map((msg) => {
              if (msg.messageType === 2) {
                // Render as WhatsApp-like notification
                return (
                  <NotificationBubble key={msg.id}>
                    <Typography variant="caption">{msg.message}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: "block",
                        fontSize: "0.7rem",
                      }}
                    >
                      {msg.timestamp}
                    </Typography>
                  </NotificationBubble>
                );
              }

              return (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: msg.isOwn ? "flex-end" : "flex-start",
                  }}
                >
                  <Stack
                    direction={msg.isOwn ? "row-reverse" : "row"}
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <Avatar
                      src={msg?.avatar}
                      alt={msg?.sender}
                      sx={{ width: 32, height: 32 }}
                    />
                    <Typography variant="subtitle2" color="text.secondary">
                      {msg?.sender}
                    </Typography>
                  </Stack>
                  <Tooltip
                    title={
                      <List sx={{ maxWidth: 200 }}>
                        {msg.userStatus?.map((status) => (
                          <ListItem key={status?.userId}>
                            <ListItemText
                              primary={status?.username}
                              secondary={
                                status.isRead
                                  ? `Read ${new Date(
                                      status?.readAt!
                                    ).toLocaleDateString()} At ${new Date(
                                      status?.readAt!
                                    ).toLocaleTimeString()}`
                                  : "Unread"
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    }
                    placement="bottom"
                    arrow
                    open={selectedMessageId === msg.id}
                    onClose={() => setSelectedMessageId(null)}
                  >
                    <MessageBubble
                      isOwn={msg.isOwn}
                      onClick={() => {
                        setSelectedMessageId(
                          msg.id === selectedMessageId ? null : msg.id
                        );
                      }}
                    >
                      <Typography variant="body1">{msg.message}</Typography>
                      {msg.attachments &&
                        msg.attachments.map((attachment) => (
                          <Box key={attachment.id} sx={{ mt: 1 }}>
                            <Typography variant="caption">
                              Attachment:{" "}
                              <Link
                                href="#"
                                style={{ color: "#A1E3F9" }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleDownloadAttachment(attachment.fileName);
                                }}
                              >
                                {attachment.fileName}
                              </Link>
                            </Typography>
                          </Box>
                        ))}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          color={
                            msg.isOwn
                              ? "rgba(255,255,255,0.7)"
                              : "text.secondary"
                          }
                        >
                          {msg.timestamp}
                        </Typography>
                        {msg.isOwn && (
                          <Box>
                            {msg.userStatus?.some((status) => status.isRead) ? (
                              <FaCheckDouble color="#4FC3F7" />
                            ) : (
                              <FaCheck color="#BDBDBD" />
                            )}
                          </Box>
                        )}
                      </Box>
                    </MessageBubble>
                  </Tooltip>
                </Box>
              );
            })}
          </MessagesArea>
          {status !== "COMPLETED" && (
            <InputArea>
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
                  size="small"
                  InputProps={{
                    startAdornment: file && (
                      <Chip
                        label={file.name}
                        onDelete={() => setFile(null)}
                        sx={{ mr: 1 }}
                      />
                    ),
                  }}
                />
                <input
                  accept="*"
                  style={{ display: "none" }}
                  id="icon-button-file"
                  type="file"
                  onChange={handleAttachFile}
                />
                <label htmlFor="icon-button-file">
                  <IconButton color="primary" component="span">
                    <IoAttach />
                  </IconButton>
                </label>
                <IconButton
                  color="primary"
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() && !file}
                >
                  <IoSend />
                </IconButton>
              </Box>
            </InputArea>
          )}
        </ChatContainer>
      </Container>
    </div>
  );
};

export default GroupChat;
